import {
  ComedianNames,
  FastCheckoutModalOrDrawer,
} from "@punchup/ui-common/components/showsNearby";
import { LinkIcon, UserRound, ZapIcon } from "lucide-react";
import { ShowInfoRow, processShowData } from "@punchup/data/legacy/shows";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@punchup/ui-common/shadcn/ui/tooltip";
import { UserEvents, VenuePageEvents, trackEvent } from "@punchup/analytics";
import {
  useTixologiEvent,
  useTixologiWidget,
} from "@punchup/data/legacy/hooks/tixologi";

import AlmostSoldOutBadge from "./AlmostSoldOutBadge";
import { Comedian } from "@punchup/data/legacy/comedian";
import { ComedianAvatar } from "./ComedianAvatar";
import ComedianAvatarWithLink from "./ComedianAvatarWithLink";
import Image from "next/image";
import Link from "next/link";
import { MetadataBadge } from "@punchup/ui-common/components/landingPage";
import { ModalOrDrawer } from "@punchup/ui-common/components/common";
import MultipleComediansDrawer from "./MultipleComediansDrawer";
import SoldOutBadge from "./SoldOutBadge";
import { TicketIcon } from "@punchup/ui-common/components/common/icons";
import TourDateActionsPopover from "./TourDateActionsPopover";
import { User } from "@supabase/supabase-js";
import { WaitlistButton } from "./landingPage/WaitlistButton";
import { WaitlistSubscription } from "@punchup/data/client";
import { cn } from "@punchup/utils/cn";
import { copyToClipboard } from "@punchup/utils/clipboard";
import dayjs from "@punchup/utils/dayjs";
import { genWebAppURL } from "@punchup/utils/genAbsoluteURL";
import { getCDNImageURL } from "@punchup/utils/getCDNImageURL";
import { getColorBasedOnContrast } from "@punchup/utils/colorContrast";
import { isTixologiWidgetLink } from "@punchup/utils/tixologiWidgetOrigin";
import { slugify } from "@punchup/utils";
import { bodyHtmlToPlainTextClient } from "@punchup/utils/rich-text/plain-text-to-body-html";
import { toast } from "@punchup/ui-common/shadcn/ui/use-toast";
import { trackGoogleAdsConversion } from "@punchup/analytics";
import { useMediaQuery } from "@punchup/utils/hooks";
import { useState } from "react";
import { useTextOverflow } from "../hooks/useTextOverflow";
import { showPosterUrl } from "./showPosterUrl";
import { useVenueShowGroupHref } from "./show-groups/useVenueShowGroupHref";
import {
  formatVenueShowGroupDateBadge,
  formatVenueShowGroupDateRange,
  multiDayEventSpan,
  nonBreakingDayRange,
} from "./venueShowGroupDate";

export interface TourDateRowProps {
  show: ShowInfoRow;
  comedians: Comedian[];
  isFollowed?: boolean;
  internalReferrer?: string;
  user?: User;
  showingOnVenue?: boolean;
  followedComedians?: string[];
  mobileView?: boolean;
  venueLogoImg: string | null;
  onBuyTicketsClick?: () => void;
  // theme
  cardBackgroundColor: string;
  buttonBackgroundColor?: string;
  buttonLabelColor?: string;
  /**
   * When set, the show's title becomes a link to a venue "Shows" page
   * at ./shows/{slugified-title}, which auto-resolves to an All Events
   * view of every upcoming show with the same title at this venue.
   */
  venueSlug?: string;
  // When the parent batch-fetches waitlist status for many rows, pass each
  // row's subscription here so the WaitlistButton skips its own per-show
  // request. `null` = known-not-subscribed, `undefined` = parent did not
  // batch and the button should fetch on its own.
  waitlistSubscription?: WaitlistSubscription | null;
}

export default function TourDateRow({
  show,
  comedians,
  internalReferrer,
  followedComedians,
  mobileView = false,
  venueLogoImg,
  onBuyTicketsClick = () => {},
  cardBackgroundColor,
  buttonBackgroundColor = "#FF5E26",
  buttonLabelColor = "#ffffff",
  venueSlug,
  waitlistSubscription,
}: TourDateRowProps) {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const displayMobileView = mobileView || !isDesktop;

  // When the caller provides a venueSlug and the show has a title, render
  // the title as a link to the venue's auto-resolved "Show Group" page. The
  // hook returns "" when either input is missing, so an empty string here
  // means "don't render a link".
  const showGroupHref = useVenueShowGroupHref(
    venueSlug,
    show.title ? slugify(show.title) : ""
  );

  const tixologiWidget = useTixologiWidget();
  const tixologiEventData = useTixologiEvent(
    show.tixologi_event_id ?? undefined
  );
  const openTixologiWidget = tixologiWidget.open;
  const tixologiEvent = tixologiEventData.data;

  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);
  const [isComediansDrawerOpen, setIsComediansDrawerOpen] = useState(false);
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);

  const isTixologiShowUrl = isTixologiWidgetLink(show.ticket_link);

  const trackBuyTickets = () => {
    trackEvent(UserEvents.BUY_TICKETS_CLICKED, {
      VenueId: show.venue_id,
      ShowId: show.id,
      ComedianIds: comedians.map((comedian) => comedian.id),
      ComedianNames: comedians.map((comedian) => comedian.display_name),
      source: "venue_page",
      IsTixologi: isTixologiShowUrl,
      ...(internalReferrer && {
        InternalReferrer: internalReferrer,
      }),
    });

    // Track Google Ads conversion
    trackGoogleAdsConversion({
      showId: show.id,
      venueId: show.venue_id,
      comedianIds: comedians.map((comedian) => comedian.id),
    });

    onBuyTicketsClick();
  };

  const handleTixologiBuyTickets = () => {
    trackBuyTickets();
    openTixologiWidget(processShowData(show));
  };

  const handleCopyLinkClick = async () => {
    const showUrl = genWebAppURL(`/show/${show.id}`);
    trackEvent(UserEvents.CLICKED_SHARE_SHOW, {
      VenueId: show.venue_id,
      ShowId: show.id,
      ComedianIds: comedians.map((comedian) => comedian.id),
      ComedianNames: comedians.map((comedian) => comedian.display_name),
      Source: "venue_page",
      ...(internalReferrer && { InternalReferrer: internalReferrer }),
    });

    const result = await copyToClipboard(showUrl);

    if (result.success) {
      toast({
        title: "Link to Show Copied to Clipboard!",
        variant: "success",
      });
    } else {
      console.error("Failed to copy text:", result.error);
      toast({
        title: "Failed to copy link",
        variant: "destructive",
      });
    }
  };

  const sortComedians = (a: Comedian, b: Comedian) => {
    // prioritize in order of importance:
    // 1. the user's followed comedians
    // 2. comedians verified on Punchup (is_punchup_verified not false)
    // 3. comedians with images (about_img not null)
    const getScore = (comedian: Comedian) => {
      let score = 0;
      if (followedComedians?.includes(comedian.id)) {
        score += 3;
      }
      if (comedian.is_punchup_verified) {
        score += 2;
      }
      if (comedian.about_img) {
        score += 1;
      }
      return score;
    };

    const scoreA = getScore(a);
    const scoreB = getScore(b);

    return scoreB - scoreA;
  };

  const onComediansClick = () => {
    setIsComediansDrawerOpen(true);
  };

  const MAX_AVATARS_DESKTOP = 3;
  const MAX_AVATARS_MOBILE = 4;

  const todayDatetime = dayjs();
  const showDatetime = dayjs(show.datetime);

  // A show carries only a single start `datetime`; the end of a multi-day event
  // (e.g. a festival) lives on the linked Tixologi event, already fetched above.
  // When the span covers more than one day, `multiDay` bundles the three
  // representations either view needs; null for a single-day show.
  const span = multiDayEventSpan(tixologiEvent);
  const multiDay = span
    ? {
        rangeLabel: formatVenueShowGroupDateRange(
          span.start,
          span.end,
          span.tz
        ),
        badge: formatVenueShowGroupDateBadge(span.start, span.end, span.tz),
        dayCountLabel: `${span.dayCount} days`,
      }
    : null;

  const dayLabel = todayDatetime.isSame(showDatetime, "day")
    ? "Today"
    : showDatetime.format("ddd");
  const dateLabel = multiDay
    ? multiDay.rangeLabel
    : showDatetime.format("MMM D");
  const dateLabelDisplay = nonBreakingDayRange(dateLabel);

  const hasPresaleCode = !!show.presale_code;
  const presaleCode = show.presale_code?.trim() || "";

  // metadata_text may be rich HTML (PUN-5353); these cards show a plain-text
  // preview + a Read-more modal, so flatten it client-side — a legacy `\n`-only
  // row keeps its line breaks. Gate on the flattened text so markup with no
  // words renders no empty badge.
  const metadataText = bodyHtmlToPlainTextClient(show.metadata_text ?? "");
  const hasMetadataText = !!metadataText;
  const { ref: metadataTextRef, isOverflowing: isMetadataOverflowing } =
    useTextOverflow(metadataText);

  const isSoldOut = show.is_sold_out;
  const isLowTickets = show.flags?.includes("LOW_TICKETS");

  const comedianNamesDesktop = comedians
    .slice(0, MAX_AVATARS_DESKTOP)
    .map((comedian) => comedian.display_name || "")
    .filter((name) => name !== "");

  const comedianNamesMobile = comedians
    .slice(0, MAX_AVATARS_MOBILE)
    .map((comedian) => comedian.display_name || "")
    .filter((name) => name !== "");

  const hasComedians = comedians.length > 0;

  const renderBuyTicketsButton = (className: string) => {
    if (isSoldOut) {
      return (
        <WaitlistButton
          show={{
            ...show,
            comedian: null,
            show_comedians: comedians.map((comedian) => ({
              ...comedian,
              is_punchup_verified: comedian.is_punchup_verified || false,
              ordering: 0,
              hidden_from_comedian_page: false,
            })),
            venue_pages: [],
          }}
          style={{
            color: buttonLabelColor,
            backgroundColor: buttonBackgroundColor,
          }}
          ownerId={show.venue_id}
          isVenue={true}
          injectedSubscription={waitlistSubscription}
          className={cn(
            "btn text-[14px] md:text-[15px] font-semibold leading-[26px] flex flex-row items-center justify-center",
            buttonLabelColor ? "" : "text-white", // only make the text white if a custom label color is not defined
            className
          )}
        />
      );
    }

    const sharedClassName = cn(
      "btn text-[14px] md:text-[15px] font-semibold leading-[26px] flex flex-row items-center justify-center",
      className
    );
    const sharedStyle = {
      color: buttonLabelColor,
      backgroundColor: buttonBackgroundColor,
    };

    if (isTixologiShowUrl) {
      return (
        <button
          type="button"
          onClick={handleTixologiBuyTickets}
          className={sharedClassName}
          style={sharedStyle}
        >
          <ZapIcon className="w-6 h-6 mr-2" />
          Fast Checkout
        </button>
      );
    }

    // External vendor: render as a real anchor so the GA4 linker can
    // auto-decorate `_gl` onto the outbound URL on click.
    return (
      <a
        href={show.ticket_link ?? "#"}
        target="_blank"
        rel="noopener"
        onClick={trackBuyTickets}
        className={sharedClassName}
        style={sharedStyle}
      >
        <TicketIcon className="w-6 h-6 mr-2 fill-white" />
        Buy Tickets
      </a>
    );
  };

  if (!displayMobileView) {
    // desktop view
    return (
      <div className="rounded-none">
        <div className="grid  grid-cols-[80px_160px_1fr_130px_40px_175px] gap-3 gap-y-0 gap-x-5 items-center p-4 px-0">
          {/* 1st column: date and day */}
          {showGroupHref ? (
            <Link
              href={showGroupHref}
              className="flex flex-col transition-colors hover:underline"
            >
              <span className="text-sm text-secondary-blue">{dayLabel}</span>
              <span className="text-lg font-bold text-primary-blue">
                {dateLabelDisplay}
              </span>
            </Link>
          ) : (
            <div className="flex flex-col">
              <span className="text-sm text-secondary-blue">{dayLabel}</span>
              <span className="text-lg font-bold text-primary-blue">
                {dateLabelDisplay}
              </span>
            </div>
          )}

          {/* 2nd column: comedian avatars (only if comedians exist) */}
          {hasComedians ? (
            <div className="flex items-center">
              <div className="relative flex -space-x-5">
                {comedians
                  .slice(0, MAX_AVATARS_DESKTOP)
                  .sort(sortComedians)
                  .map((comedian, index) => (
                    <div
                      key={comedian.id}
                      className="relative"
                      style={{ zIndex: MAX_AVATARS_DESKTOP - index }}
                    >
                      <ComedianAvatarWithLink
                        comedianSlug={comedian.slug}
                        comedianImgUrl={
                          comedian.about_img
                            ? getCDNImageURL(comedian.about_img)
                            : undefined
                        }
                        comedianName={comedian.display_name || ""}
                        size="md"
                        className="relative"
                        isFollowed={followedComedians?.includes(comedian.id)}
                        isNotPunchupComedian={!comedian.is_punchup_verified}
                        hideComedianName
                        hideFollowIndicator={
                          index === MAX_AVATARS_DESKTOP - 1 &&
                          comedians.length > MAX_AVATARS_DESKTOP
                        }
                      />
                      {index === MAX_AVATARS_DESKTOP - 1 &&
                        comedians.length > MAX_AVATARS_DESKTOP && (
                          <TooltipProvider>
                            <Tooltip delayDuration={100}>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => setIsComediansDrawerOpen(true)}
                                  className="border-[1.5px] border-primary w-full h-full left-0 top-0 absolute bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center z-10 transition-colors duration-200"
                                >
                                  <div className="flex gap-[2px] group">
                                    <div className="w-[3px] h-[3px] rounded-full bg-white group-hover:scale-110 transition-transform" />
                                    <div className="w-[3px] h-[3px] rounded-full bg-white group-hover:scale-110 transition-transform" />
                                    <div className="w-[3px] h-[3px] rounded-full bg-white group-hover:scale-110 transition-transform" />
                                  </div>
                                </button>
                              </TooltipTrigger>
                              <TooltipContent
                                sideOffset={10}
                                className="hidden text-base md:block"
                              >
                                See more
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                    </div>
                  ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-row justify-start items-center">
              <div className="h-10 w-10 md:w-16 md:h-16 border border-neutral-200 bg-neutral-100 rounded-full flex flex-col justify-center items-center z-10">
                <UserRound className="h-6 w-6 md:w-10 md:h-10 stroke-neutral-300 z-20" />
              </div>
            </div>
          )}

          {/* 3rd column: show title, comedian names, and presale code (if exists) */}
          <div className={"flex flex-col gap-1"}>
            {show.title ? (
              showGroupHref ? (
                <Link
                  href={showGroupHref}
                  className="text-lg font-bold text-primary-blue transition-colors hover:underline"
                >
                  {show.title}
                </Link>
              ) : (
                <p className="text-lg font-bold text-primary-blue">
                  {show.title}
                </p>
              )
            ) : hasComedians ? (
              <ComedianNames comedianNames={comedianNamesDesktop} />
            ) : null}
            {hasMetadataText && (
              <span className="overflow-hidden text-sm font-normal text-neutral-600">
                <MetadataBadge text={metadataText} lineClamp={1} />
              </span>
            )}
            {hasPresaleCode && (
              <span className="pt-1 overflow-hidden text-sm font-normal text-neutral-600">
                <MetadataBadge text={`CODE: ${presaleCode}`} />
              </span>
            )}
          </div>

          {/* 4th column: show time (or day count for multi-day), sold-out badge */}
          {/* A multi-day span shows the number of days instead of a time — the
              time is ambiguous across days and surfaces at checkout. */}
          <div className="flex flex-col items-center gap-y-1">
            {multiDay ? (
              <span className="block text-lg font-semibold">
                {multiDay.dayCountLabel}
              </span>
            ) : showGroupHref ? (
              <Link
                href={showGroupHref}
                className="block text-lg font-semibold transition-colors hover:underline"
              >
                {dayjs(show.datetime).format("h:mma")}
              </Link>
            ) : (
              <span className="block text-lg font-semibold">
                {dayjs(show.datetime).format("h:mma")}
              </span>
            )}
            {isSoldOut ? (
              <SoldOutBadge />
            ) : (
              !isSoldOut && isLowTickets && <AlmostSoldOutBadge />
            )}
          </div>

          {/* 5th column: copy link button */}
          <div className="flex justify-center">
            <TooltipProvider>
              <Tooltip delayDuration={100}>
                <TooltipTrigger asChild>
                  <button
                    className="p-2 text-[#949494] hover:text-neutral-600 transition-colors ease-in-out duration-200"
                    title="Copy Link to Show"
                    onClick={handleCopyLinkClick}
                  >
                    <LinkIcon className="w-5 h-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent sideOffset={10}>
                  Copy link to show
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {/* 6th column: buy tickets button */}
          <div className="flex justify-end w-full">
            {renderBuyTicketsButton("w-full py-3 px-4")}
          </div>
        </div>

        <MultipleComediansDrawer
          isOpen={isComediansDrawerOpen}
          setIsOpen={setIsComediansDrawerOpen}
          comedians={comedians}
          followedComedians={followedComedians || []}
        />

        <FastCheckoutModalOrDrawer
          show={processShowData(show)}
          comedianNames={comedianNamesDesktop}
          isOpen={checkoutModalOpen}
          setIsOpen={setCheckoutModalOpen}
        />
      </div>
    );
  }

  const posterUrl = showPosterUrl(show, tixologiEvent);

  // mobile view
  return (
    <div className="rounded-[10px] shadow-[0px_0px_4px_0px_rgba(0,0,0,0.25)] h-full flex flex-col">
      <div className="flex flex-col h-full relative">
        {/* image at the top */}
        <div className="rounded-t-lg flex-1 h-full relative overflow-hidden">
          <MobileCardImage
            href={showGroupHref}
            posterUrl={posterUrl}
            venueLogoImg={venueLogoImg}
            cardBackgroundColor={cardBackgroundColor}
            hasComedians={hasComedians}
          />

          {/* "actions" button overlayed on image */}
          <div className="absolute right-1.5 top-2 z-30">
            <TourDateActionsPopover
              copyUrl={`/e/${show.id}`}
              venueSlug={venueSlug}
              groupSlug={show.title ? slugify(show.title) : undefined}
              shareData={{
                title: show.title ?? "",
                text: "Check out this show at Punchup",
              }}
              triggerColor={getColorBasedOnContrast(
                cardBackgroundColor,
                "#000000",
                "#ffffff"
              )}
              onCopyLink={() => {
                trackEvent(UserEvents.CLICKED_SHARE_SHOW, {
                  VenueId: show.venue_id,
                  ShowId: show.id,
                  ComedianIds: comedians.map((comedian) => comedian.id),
                  ComedianNames: comedians.map(
                    (comedian) => comedian.display_name
                  ),
                  Source: "venue_page",
                  ...(internalReferrer && {
                    InternalReferrer: internalReferrer,
                  }),
                });
              }}
            />
          </div>

          {/* date time overlay on image */}
          <MobileDateTimeOverlay
            href={showGroupHref}
            datetime={show.datetime}
            multiDay={multiDay ?? undefined}
          />

          {/* comedians overlay on image */}
          {hasComedians && (
            <button
              type="button"
              onClick={onComediansClick}
              className="outline-none border-none absolute bottom-3 left-3 right-3 flex flex-row items-center justify-start gap-x-1.5 bg-black/40 backdrop-blur-3xl px-1 py-0.5 md:px-2 rounded-lg z-30"
            >
              <div className="flex -space-x-3">
                {comedians
                  .slice(0, MAX_AVATARS_MOBILE)
                  .sort(sortComedians)
                  .map((comedian, index) => (
                    <div
                      key={comedian.id}
                      className="relative"
                      style={{ zIndex: MAX_AVATARS_MOBILE - index }}
                    >
                      <div className="rounded-full border border-white">
                        <ComedianAvatar
                          comedianImgUrl={
                            comedian.about_img
                              ? getCDNImageURL(comedian.about_img)
                              : undefined
                          }
                          comedianName={comedian.display_name || ""}
                          size="xs"
                          className="relative"
                          isFollowed={followedComedians?.includes(comedian.id)}
                          isNotPunchupComedian={!comedian.is_punchup_verified}
                        />
                      </div>
                      {/* "three dots" avatar button */}
                      {index === MAX_AVATARS_MOBILE - 1 &&
                        comedians.length > MAX_AVATARS_MOBILE && (
                          <div className="border border-white w-full h-full left-0 top-0 absolute bg-black/50 rounded-full flex items-center justify-center z-10">
                            <span className="text-white text-xs font-regular leading-none mt-px">
                              +{comedians.length - MAX_AVATARS_MOBILE}
                            </span>
                          </div>
                        )}
                    </div>
                  ))}
              </div>
              <div className="flex-1 text-left text-white font-regular text-sm">
                <div className="line-clamp-1 break-words">
                  {comedians
                    .slice(0, MAX_AVATARS_MOBILE)
                    .sort(sortComedians)
                    .map((comedian) => comedian.display_name)
                    .join(", ")}
                </div>
              </div>
            </button>
          )}
        </div>

        {/* footer with text info */}
        <div className="flex flex-col gap-3 px-4 pt-3 pb-4 min-h-[158px]">
          <div className="flex flex-col flex-1 justify-start text-lg font-bold text-primary-blue">
            <div className={cn((hasMetadataText || hasPresaleCode) && "pb-1")}>
              {show.title ? (
                showGroupHref ? (
                  <Link
                    href={showGroupHref}
                    className="text-base font-semibold text-primary-blue line-clamp-1 transition-colors hover:underline"
                  >
                    {show.title}
                  </Link>
                ) : (
                  <p className="text-base font-semibold text-primary-blue line-clamp-1">
                    {show.title}
                  </p>
                )
              ) : hasComedians ? (
                <ComedianNames comedianNames={comedianNamesMobile} />
              ) : null}
            </div>
            {/* always render the metadata text container to maintain consistency across cards */}
            <div className="overflow-hidden text-sm font-normal text-neutral-600">
              {hasMetadataText && (
                <button
                  type="button"
                  className="relative block text-left w-full appearance-none bg-transparent border-none p-0 m-0 font-[inherit] text-[inherit] leading-[inherit]"
                  onClick={
                    isMetadataOverflowing
                      ? () => {
                          trackEvent(
                            VenuePageEvents.SHOW_DESCRIPTION_READ_MORE_CLICKED,
                            {
                              ShowId: show.id,
                              VenueId: show.venue_id,
                            }
                          );
                          setIsDescriptionModalOpen(true);
                        }
                      : undefined
                  }
                >
                  <p
                    ref={metadataTextRef}
                    className={cn(
                      "overflow-hidden text-ellipsis",
                      hasPresaleCode ||
                        isSoldOut ||
                        (!isSoldOut && isLowTickets)
                        ? "line-clamp-1"
                        : "line-clamp-2"
                    )}
                  >
                    {metadataText}
                  </p>
                  {isMetadataOverflowing && (
                    <span className="absolute bottom-0 right-0 inline-flex items-baseline">
                      <span className="w-8 bg-gradient-to-l from-white to-transparent">
                        &zwj;
                      </span>
                      <span className="bg-white text-primary-blue font-medium">
                        ... Read more
                      </span>
                    </span>
                  )}
                </button>
              )}
            </div>
            <div
              className={cn(
                "flex flex-row gap-x-2 items-center justify-between w-full text-sm font-normal text-neutral-600",
                (hasPresaleCode || isSoldOut || (!isSoldOut && isLowTickets)) &&
                  "pt-1"
              )}
            >
              {hasPresaleCode && (
                <div className="flex-1 overflow-hidden">
                  <MetadataBadge
                    text={`CODE: ${presaleCode}`}
                    className={
                      isSoldOut || (!isSoldOut && isLowTickets) ? "" : "w-full"
                    }
                  />
                </div>
              )}

              {/* sold out or low tickets badge, if applicable */}
              {isSoldOut ? (
                <SoldOutBadge className={!hasPresaleCode ? "w-full" : ""} />
              ) : (
                !isSoldOut &&
                isLowTickets && (
                  <AlmostSoldOutBadge
                    className={!hasPresaleCode ? "w-full" : ""}
                  />
                )
              )}
            </div>
          </div>

          <div className="flex justify-end w-full">
            {renderBuyTicketsButton("w-full py-3 px-4")}
          </div>
        </div>

        <MultipleComediansDrawer
          isOpen={isComediansDrawerOpen}
          setIsOpen={setIsComediansDrawerOpen}
          comedians={comedians}
          followedComedians={followedComedians || []}
        />

        <FastCheckoutModalOrDrawer
          show={processShowData(show)}
          comedianNames={comedianNamesMobile}
          isOpen={checkoutModalOpen}
          setIsOpen={setCheckoutModalOpen}
        />

        {hasMetadataText && (
          <ModalOrDrawer
            isOpen={isDescriptionModalOpen}
            onOpenChange={setIsDescriptionModalOpen}
            className="z-[99] md:max-h-[75dvh] md:overflow-y-scroll"
          >
            <div className="flex flex-col gap-4 pt-2 pb-4 overflow-y-scroll md:overflow-hidden">
              {show.title && (
                <h2 className="text-xl font-semibold text-primary-blue font-sans normal-case">
                  {show.title}
                </h2>
              )}
              <p className="text-base text-neutral-700 whitespace-pre-wrap break-words leading-relaxed">
                {metadataText}
              </p>
            </div>
          </ModalOrDrawer>
        )}
      </div>
    </div>
  );
}

function MobileCardImage({
  href,
  posterUrl,
  venueLogoImg,
  cardBackgroundColor,
  hasComedians,
}: {
  href: string;
  posterUrl: string | null;
  venueLogoImg: string | null;
  cardBackgroundColor: string;
  hasComedians: boolean;
}) {
  if (posterUrl) {
    const imageContent = (
      <>
        {/* Blurred background - fills entire space */}
        <div className="absolute inset-0 overflow-hidden">
          <Image
            src={posterUrl}
            alt=""
            fill
            className="object-cover blur-md scale-110"
            sizes="(max-width: 768px) 100vw, 400px"
          />
        </div>
        {/* Main image - contained, shows full poster */}
        <div className="absolute inset-0 z-10 p-4">
          <Image
            src={posterUrl}
            alt="show poster image"
            fill
            className="object-contain"
            sizes="(max-width: 768px) 100vw, 400px"
          />
        </div>
      </>
    );

    return href ? (
      <Link href={href} className="absolute inset-0">
        {imageContent}
      </Link>
    ) : (
      <div className="absolute inset-0">{imageContent}</div>
    );
  }

  // TODO: add a placeholder image, something in case the venue has no logo
  const fallbackContent = venueLogoImg ? (
    <div className={cn(hasComedians && "pb-[58px]")}>
      <Image
        alt="venue logo"
        src={getCDNImageURL(venueLogoImg)}
        className="h-[100px] w-auto md:max-w-md"
        width={100}
        height={100}
        sizes="100px"
      />
    </div>
  ) : null;

  return href ? (
    <Link
      href={href}
      className="w-full h-full flex flex-row justify-center items-center py-4 md:py-8 px-6"
      style={{ backgroundColor: cardBackgroundColor }}
    >
      {fallbackContent}
    </Link>
  ) : (
    <div
      className="w-full h-full flex flex-row justify-center items-center py-4 md:py-8 px-6"
      style={{ backgroundColor: cardBackgroundColor }}
    >
      {fallbackContent}
    </div>
  );
}

function MobileDateTimeOverlay({
  href,
  datetime,
  multiDay,
}: {
  href: string;
  datetime: ShowInfoRow["datetime"];
  // Set when the show spans multiple days: render the range badge + day count
  // instead of the single-day date + time. `badge` shape comes from
  // `formatVenueShowGroupDateBadge`.
  multiDay?: {
    badge: ReturnType<typeof formatVenueShowGroupDateBadge>;
    dayCountLabel: string;
  };
}) {
  const badge = multiDay?.badge;
  // A multi-day span widens the box (min-w + px) to fit the range; a single day
  // keeps the fixed 48px square. Both share the same white chip shell.
  const dateBox = badge ? (
    <div className="bg-white text-primary-blue rounded-lg h-[50px] min-w-[48px] px-2 flex flex-col items-center justify-center shadow-sm">
      {"day" in badge ? (
        // Same-month range: "AUG" over "14–15".
        <div className="flex flex-col items-center justify-center gap-y-[3px] select-none">
          <span className="text-xl font-semibold leading-none whitespace-nowrap">
            {badge.day}
          </span>
          <span className="uppercase text-xs leading-none">{badge.month}</span>
        </div>
      ) : (
        // Cross-month span: "JUL 14 –" over "DEC 29", two equal-weight lines.
        <div className="flex flex-col items-center justify-center gap-y-[3px] select-none uppercase text-xs font-semibold leading-none">
          <span className="whitespace-nowrap">{badge.start}</span>
          <span className="whitespace-nowrap">{badge.end}</span>
        </div>
      )}
    </div>
  ) : (
    <div className="bg-white text-primary-blue rounded-lg h-[50px] w-[48px] flex flex-col items-center justify-center shadow-sm">
      <time
        dateTime="2018-07-07"
        className="flex flex-col items-center justify-center gap-y-[3px] select-none"
      >
        <span className="text-xl font-semibold leading-none">
          {dayjs(datetime).format("D")}
        </span>
        <span className="uppercase text-xs leading-none">
          {dayjs(datetime).format("MMM")}
        </span>
      </time>
    </div>
  );

  // The second chip shows the day count for a multi-day span (the time is
  // ambiguous across days and surfaces at checkout), else the single show time.
  // For a multi-day span it drops its fixed width and stretches (the column
  // flex stretches children) to match the wider date-range badge above it.
  const detailBox = (
    <div
      className={cn(
        "bg-white text-primary-blue rounded-md py-1 flex flex-col items-center justify-center shadow-sm",
        multiDay ? "min-w-[48px] px-2" : "w-[60px]"
      )}
    >
      <span className="font-semibold text-xs leading-none mt-0.5 select-none">
        {multiDay ? multiDay.dayCountLabel : dayjs(datetime).format("h:mma")}
      </span>
    </div>
  );

  const overlayContent = (
    <>
      {dateBox}
      {detailBox}
    </>
  );

  return href ? (
    <Link
      href={href}
      className="absolute left-3 top-3 flex flex-col gap-y-1.5 z-30"
    >
      {overlayContent}
    </Link>
  ) : (
    <div className="absolute left-3 top-3 flex flex-col gap-y-1.5 z-30">
      {overlayContent}
    </div>
  );
}
